import {
  Box,
  Button,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { InputFilter, InputFilterField } from "./InputFilter";
import { SelectFilter, SelectFilterField } from "./SelectFilter";
import { FilterType } from "./FilterBase";
import { FaFilter } from "react-icons/fa6";
import { useState } from "react";
import { MobileRoute, MobileRouteHeaderProps } from "../MobileRoute";
import { MultiSelectFilterField, MultiSelectFilter } from "./MultiSelectFilter";
import { IoChevronBack, IoCloseOutline, IoInformationCircle } from "react-icons/io5";
import { DatepickerFilter, DatepickerFilterField } from "./DatepickerFilter";
import { getYCenterStyles } from "../../theme/mixins";

export type FilterField =
  | SelectFilter<any>
  | InputFilter
  | MultiSelectFilter<any>
  | DatepickerFilter;

export type FilterProps = {
  filters: Array<FilterField>;
};

export const Filter = ({ filters }: FilterProps) => {
  const theme = useTheme();

  const onlyMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const onlySmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const mainInputFilter: InputFilter | undefined = filters.find(
    (filter: FilterField) => filter.type === FilterType.INPUT && filter.main
  ) as InputFilter | undefined;

  const resetFilter = () => {
    filters.forEach((filter) => filter.resetFilter());
  };

  const isEmptyFilter = filters.every((filter) => !filter.hasValue);

  const { Popover, Trigger } = usePopoverFilter({
    filters,
    resetFilter,
  });

  let fieldLimit = 4;

  if (onlyMediumScreen) fieldLimit = 2;

  if (onlySmallScreen) fieldLimit = 0;

  return (
    <Box
      px={2}
      height="65px"
      borderRadius={1}
      bgcolor={theme.palette.grey[100]}
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
    >
      <Box display="flex" gap={2} alignItems="center" width="100%">
        <FaFilter size="18px" color={theme.palette.primary.main} />

        {mainInputFilter && (
          <TextField
            fullWidth
            variant="standard"
            placeholder={mainInputFilter.placeholder}
            InputProps={{
              disableUnderline: true,
            }}
            onChange={(event) => mainInputFilter.onChange(event.target.value)}
          />
        )}
      </Box>

      <Box display="flex" gap={2} alignItems="center">
        <FilterFields filters={filters} limit={fieldLimit}></FilterFields>

        {filters.length > fieldLimit && Trigger}

        {!isEmptyFilter && (
          <IconButton onClick={() => resetFilter()}>
            <IoCloseOutline />
          </IconButton>
        )}
      </Box>

      {Popover}
    </Box>
  );
};

const usePopoverFilter = ({
  filters,
  resetFilter,
}: FilterProps & { resetFilter: () => void }): {
  Popover: JSX.Element;
  Trigger: JSX.Element;
} => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);

  const theme = useTheme();

  const onlySmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const onResetFilter = () => {
    resetFilter();
    setAnchorEl(null);
  };

  const getPopover = (): JSX.Element => {
    if (onlySmallScreen && anchorEl) {
      return (
        <MobileFilter
          onClose={() => setAnchorEl(null)}
          filters={filters}
          onResetFilter={onResetFilter}
        />
      );
    }

    if (onlySmallScreen && !open) return <></>;

    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box display="flex" flexDirection="column">
          <Box
            p={2}
            minWidth="460px"
            display="grid"
            gridTemplateColumns="repeat(2, 1fr)"
            gap={2}
            borderBottom={`1px solid ${theme.palette.divider}`}
          >
            <FilterFields filters={filters} outlined={true}></FilterFields>
          </Box>

          <Box p={1} display="flex" justifyContent="flex-end">
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={onResetFilter}
            >
              Vymazať filter
            </Button>
          </Box>
        </Box>
      </Popover>
    );
  };

  return {
    Popover: getPopover(),
    Trigger: (
      <OpenFiltersPopoverTrigger
        filters={filters}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      />
    ),
  };
};

const OpenFiltersPopoverTrigger = ({
  onClick,
  filters,
}: {
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  filters: FilterField[];
}) => {
  const theme = useTheme();

  const filtersWithValues = filters.filter((filter) => {
    const value = filter.getValue();
    if (Array.isArray(value)) return value.length > 0;
    return !!value;
  });

  const onlySmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const buttonText = onlySmallScreen ? "Otvoriť filtre" : "Dalšie filtre";

  return (
    <Button variant="text" style={{ whiteSpace: "nowrap" }} onClick={onClick}>
      <strong>{buttonText}</strong>
      {filtersWithValues.length > 0 && (
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderRadius: "50%",
            width: "24px",
            height: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginLeft: "8px",
            lineHeight: 1,
            fontSize: 12,
          }}
        >
          {filtersWithValues.length}
        </Box>
      )}
    </Button>
  );
};

type FilterFieldsProps = Omit<FilterProps, "onClear"> & {
  limit?: number;
  outlined?: boolean;
};

const FilterFields = ({ filters, limit, outlined }: FilterFieldsProps) => {
  const theme = useTheme();

  let slice = filters;

  if (limit === 0) slice = [];

  if (limit) slice = filters.slice(0, limit);

  const getFilterField = (filter: FilterField, index: number) => {
    if (filter.type === FilterType.SELECT) {
      return (
        <SelectFilterField
          key={index + 1}
          {...filter}
          outlined={outlined}
        />
      );
    }
    if (filter.type === FilterType.MULTI_SELECT) {
      return (
        <MultiSelectFilterField
          key={index + 1}
          {...filter}
          outlined={outlined}
        />
      );
    }
    if (filter.type === FilterType.DATEPICKER) {
      return (
        <DatepickerFilterField
          key={index + 1}
          {...filter}
          outlined={outlined}
        />
      )
    }
    if (filter.type === FilterType.INPUT && !filter.main) {
      return (
        <InputFilterField
          key={index + 1}
          {...filter}
          outlined={outlined}
        />
      )
    }
  }

  return (
    <>
      {slice.map((filter, index) => {
        const field = getFilterField(filter, index);

        if (!filter.tooltipBuilder) return field;


        return <Box key={index + 1} sx={{ ...getYCenterStyles(), gap: 1 }}>
          <Tooltip title={filter.tooltipBuilder()}>
            <Box>
              <IoInformationCircle size={24} color={theme.palette.grey[600]} />
            </Box>
          </Tooltip>
          {field}
        </Box>
      })}
    </>
  );
};

export const FilterMobileRouteHeader = ({
  title,
  onClose,
  onClear,
  height,
}: MobileRouteHeaderProps & { onClear: () => void; height: number; }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        borderBottom: 1,
        borderColor: theme.palette.divider,
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        height: `${height}px`,
        flexShrink: 0,
        paddingX: theme.spacing(2),
      }}
    >
      <Box display="flex" alignItems="center" flexGrow={1} gap={1}>
        <IconButton
          color="primary"
          aria-label="go back"
          onClick={() => onClose()}
          edge="start"
        >
          <IoChevronBack size={24} />
        </IconButton>

        {/* TODO: OVERFLOW FOR TEXT */}
        <Typography variant="h5" noWrap>
          {title}
        </Typography>
      </Box>

      <Button onClick={onClear} size="medium">
        Vymazať filter
      </Button>
    </Box>
  );
};

type MobileFilterProps = {
  onClose: () => void;
  filters: FilterField[];
  onResetFilter: () => void;
};

const MobileFilter = ({
  onClose,
  filters,
  onResetFilter,
}: MobileFilterProps) => {
  const headerHeight = 75;

  const boxStyles = {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
  };

  const applyAllFilters = () => {
    filters.forEach((filter) => filter.onChange(filter.localValue));
    onClose();
  };

  const rollbackAllFilters = () => {
    filters.forEach((filter) => filter.rollbackFilter());
    onClose();
  };

  return (
    <MobileRoute
      onClose={onClose}
      headerBuilder={(onClose) => (
        <FilterMobileRouteHeader
          height={headerHeight}
          onClose={() => {
            rollbackAllFilters();
            onClose();
          }}
          title="Filter"
          onClear={onResetFilter}
        />
      )}
    >
      <Box sx={{
        ...boxStyles,
        height: `calc(100vh - ${headerHeight}px)`,
      }}>
        <Box sx={{
          ...boxStyles,
          overflowY: "auto",
        }}>
          <FilterFields filters={filters} outlined={true}></FilterFields>
        </Box>

        <Box
          sx={(theme) => ({
            ...boxStyles,
            flexGrow: 0,
            flexShrink: 0,
            height: 100,
            borderTop: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(2),
            justifyContent: "stretch",
          })}
        >
          <Button
            onClick={applyAllFilters}
            size="large"
            sx={{ height: "100%" }}
          >
            Použiť filter
          </Button>
        </Box>
      </Box>
    </MobileRoute>
  );
};
